import { clsx } from 'clsx'
import { useSearchParams } from 'react-router-dom'

import { Chat, ChatType } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useAuthMember,
  useUnreadMessagesCount,
} from '@tribeplatform/react-sdk/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { IndicatorBadgeFlipper } from '@tribeplatform/react-ui-kit/IndicatorBadge'

import { Link } from '../../../Link/Link.js'
import { ChatImage } from '../../components/ChatImage.js'
import {
  IsTypingIndicatorHoC,
  IsTypingIndicatorRenderer,
} from '../../components/IsTypingIndicator.js'
import { getChatDetails } from '../../utils.js'
import { Timestamp } from '../ChatMessage/Timestamp.js'
import {
  getLastMessageAuthorName,
  isChatToMyself,
} from '../utils/chat.utils.js'

import { LastMessagePreview } from './LastMessagePreview.js'
import { SelfChatItemName } from './SelfChatItemName.js'

interface Props {
  chat: Chat
  className?: string
  isActive?: boolean
  onClick?: () => void
}

export const ChatListItem = ({ chat, className, isActive, onClick }: Props) => {
  const { data: authMember } = useAuthMember()
  const { unreadMessagesCount } = useUnreadMessagesCount()
  const intl = useI18n()
  const { $t } = intl
  const [searchParams] = useSearchParams()

  if (!chat) {
    return null
  }

  const unreadCount = unreadMessagesCount[chat._id]
  const { lastMessage } = chat
  const lastMessageTimestamp = lastMessage?.sentAt
  const lastMessageAuthorName = getLastMessageAuthorName({
    lastMessage,
    intl,
    authMember,
  })

  const { link, name, imageUrl } = getChatDetails({
    chat,
    authMember,
    intl,
  })
  const isSavedChat = isChatToMyself(chat)
  const isGroupChat = chat.type === ChatType.Group
  const showAuthorName =
    lastMessageAuthorName !== name && !lastMessage?.meta && !isSavedChat

  return (
    <Link href={`${link}?${searchParams.toString()}`} className="block">
      <div
        className={clsx(
          'relative px-2 py-3 bg-surface hover:bg-surface-hovered transition-all duration-300 rounded-base cursor-pointer',
          'group flex flex-1 gap-x-2 items-center overflow-hidden',
          isActive && 'bg-surface-subdued',
          className,
        )}
        onClick={onClick}
      >
        <ChatImage
          size="2.5x"
          name={name}
          imageUrl={imageUrl}
          type={chat.type}
        />
        <div className="flex-grow min-w-0">
          <div className="flex space-s-2 items-center min-w-0">
            <div className="flex items-center grow truncate">
              {isGroupChat && (
                <SvgIcon
                  name="users"
                  className="mr-2 inline-block text-content-subdued"
                />
              )}
              {isSavedChat && (
                <SvgIcon
                  name="star"
                  className="mr-2 inline-block text-content-subdued"
                />
              )}
              <div className="font-semibold text-sm text-content truncate">
                {isSavedChat ? <SelfChatItemName name={name} /> : name}
              </div>
            </div>
            <Timestamp sentAt={lastMessageTimestamp} />
          </div>
          <div className="flex items-center overflow-hidden mt-1 min-w-0 relative gap-x-4">
            <div className="min-w-0 grow">
              <div className="flex-1 space-s-1 flex items-center text-sm text-content-subdued max-w-full whitespace-nowrap truncate">
                {showAuthorName && (
                  <div className="font-medium truncate max-w-16 flex-shrink-0 inline-block">
                    <T
                      id="Chats.Preview.Author"
                      defaultMessage="{author_name}: "
                      values={{ author_name: lastMessageAuthorName }}
                    />
                  </div>
                )}
                <LastMessagePreview message={lastMessage} />
              </div>
              <IsTypingIndicatorHoC chatId={chat._id} subscriptionDelay={2000}>
                {(props, deferredProps, ref) => (
                  <IsTypingIndicatorRenderer
                    currentProps={props}
                    deferredProps={deferredProps}
                    ref={ref}
                    className={clsx(
                      'w-full absolute left-0 top-0 margin-0 text-xs h-full hover:bg-surface-hovered',
                      isActive ? 'bg-surface-subdued' : ' bg-surface',
                    )}
                  />
                )}
              </IsTypingIndicatorHoC>
            </div>
            <IndicatorBadgeFlipper
              count={unreadCount}
              variant="primary"
              title={$t(
                {
                  defaultMessage:
                    '{unread_count, plural, 1 {# unread message} other {# unread messages}}',
                  id: 'Messaging.Indicator.UnreadCount',
                },
                {
                  unread_count: unreadCount,
                },
              )}
              showZero={false}
            />
          </div>
        </div>
      </div>
    </Link>
  )
}
